import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import { graphql } from '@/gql';
import {
  CreateEstimateInput,
  CreateEstimateMutation as CreateEstimateMutationResult,
} from '@/gql/graphql';
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

const CreateEstimateMutation = graphql(`
  mutation CreateEstimate($input: CreateEstimateInput!) {
    createEstimate(input: $input) {
      id
    }
  }
`);

const useCreateEstimate = (
  options?: UseMutationOptions<
    CreateEstimateMutationResult,
    Error,
    CreateEstimateInput
  > & {
    /** Project identity used by the route's estimate query. */
    cacheProjectId?: string;
  }
) => {
  const queryClient = useQueryClient();
  const { onSuccess, cacheProjectId, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: (input) => {
      return gqlClient.request(CreateEstimateMutation, { input });
    },
    onSuccess: async (data, variables, context) => {
      const routeProjectId = cacheProjectId ?? variables.projectId;
      try {
        await Promise.all([
          queryClient.invalidateQueries(
            {
              queryKey: projectKeys.estimates(routeProjectId),
              // The list is inactive while the designer is open. Refresh it
              // before the builder mounts against its two-minute warm cache.
              ...(cacheProjectId && {
                exact: true,
                refetchType: 'all' as const,
              }),
            },
            { throwOnError: true }
          ),
          ...(routeProjectId !== variables.projectId
            ? [
                queryClient.invalidateQueries(
                  {
                    queryKey: projectKeys.estimates(variables.projectId),
                    exact: true,
                  },
                  { throwOnError: true }
                ),
              ]
            : []),
        ]);
      } catch (cause) {
        throw new Error(
          'The estimate was created, but its project list could not be refreshed. Reload the project before creating another estimate.',
          { cause }
        );
      }
      await onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useCreateEstimate;
